import { type FC } from "react";

import { OptionCard } from "@/shared/components/ui/OptionCard";
import { FieldError } from "@/shared/components/ui/FieldError";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const ISSUE_OPTIONS = [
  {
    value: "staples_removed",
    label: "Staples have been removed, replaced, or show extra staple holes",
  },
  {
    value: "pages_damaged",
    label: "Pages are torn, water-damaged, or stained",
  },
  {
    value: "handwritten_notes",
    label: "Handwritten notes, cross-outs, or pencil marks were added after signing",
  },
];

const NONE_VALUE = "none";

export const WillConditionScreen: FC<FlowCustomComponentProps> = ({ answers, setAnswer, errors }) => {
  const selected: string[] = Array.isArray(answers["will_condition_issues"])
    ? (answers["will_condition_issues"] as string[])
    : [];

  const isNoneSelected = selected.includes(NONE_VALUE);

  const handleSelect = (value: string) => {
    if (value === NONE_VALUE) {
      setAnswer("will_condition_issues", selected.includes(NONE_VALUE) ? [] : [NONE_VALUE]);
    } else {
      const without = selected.filter((v) => v !== NONE_VALUE);
      const next = without.includes(value)
        ? without.filter((v) => v !== value)
        : [...without, value];
      setAnswer("will_condition_issues", next);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">Is the will in good condition?</p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Check any issues that apply to the will or codicils.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {ISSUE_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            label={option.label}
            selected={selected.includes(option.value)}
            onClick={() => handleSelect(option.value)}
            variant="checkbox"
          />
        ))}

        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 border-t border-primary" />
          <span className="text-sm text-secondary">OR</span>
          <div className="flex-1 border-t border-primary" />
        </div>

        <OptionCard
          label="None, the document is completely intact and unaltered"
          selected={isNoneSelected}
          onClick={() => handleSelect(NONE_VALUE)}
          variant="checkbox"
        />
        <FieldError message={errors?.will_condition_issues} />
      </div>
    </div>
  );
};
